'use client'

// We can not useState or useRef in a server component, which is why we are
// extracting this part out into its own file with 'use client' on top
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import React from "react";
import {ClientConfigProvider, ClientConfigType} from "@/app/config/configProvider";
import {ToastProvider} from "@/components/Toast";
import {initializeAppInsights} from "@/lib/appInsights";
import '@/components/NFC/MockNDEFReader';

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // With SSR, we usually want to set some default staleTime
                // above 0 to avoid refetching immediately on the client
                staleTime: 60 * 1000,
            },
        },
    })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
    if (typeof window === 'undefined') {
        // Server: always make a new query client
        return makeQueryClient()
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) browserQueryClient = makeQueryClient()
        return browserQueryClient
    }
}

function loadAppInsights(config: ClientConfigType) {
    if (typeof window === 'undefined') {
        return;
    }

    const connectionString = config.applicationInsightsConnectionString;

    if (!connectionString) {
        console.warn("Application Insights connection string not provided; skipping initialization.");
        return;
    }

    initializeAppInsights(connectionString);
}


export default function Providers({children, config}: { children: React.ReactNode, config: ClientConfigType }) {
    // NOTE: Avoid useState when initializing the query client if you don't
    //       have a suspense boundary between this and the code that may
    //       suspend because React will throw away the client on the initial
    //       render if it suspends and there is no boundary
    const queryClient = getQueryClient();

    // Initialize App Insights on mount (client-side only)
    React.useEffect(() => {
        loadAppInsights(config);
    }, [config]);

    return (
        <ClientConfigProvider config={config}>
            <QueryClientProvider client={queryClient}>
                <ToastProvider>
                    {children}
                </ToastProvider>
            </QueryClientProvider>
        </ClientConfigProvider>
    )
}
